import { type MouseEvent, useCallback } from "react";

import { IconType } from "~/common/components/icon/IconType";
import { useTranslation } from "react-i18next";
import { Button, type ButtonProps } from "~/common/mui";
import { type BaseProductElementProps } from "~/domains/product/types/BaseProductElementProps";
import { useProductContext } from "~/domains/product/ProductContext";
import { SideButton } from "~/common/components/Button/SideButton";
import {
  useAddProductToWishlistFlow,
  type UseAddProductToWishlistFlowParams,
} from "~/features/wishlist/hooks/useAddProductToWishlistFlow";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";

type ButtonBaseProps = {
  sideButton?: boolean;
} & Omit<ButtonProps, "children">;
export const AddToWishlistButtonBase = (props: ButtonBaseProps) => {
  const { t } = useTranslation();
  const { sideButton, color, variant, ...rest } = props;

  const icon = IconType.Bookmark;
  const label = t("basket.addToWishlist");

  if (sideButton) {
    return <SideButton icon={icon} data-test-id={"btn-add-to-wishlist"} {...rest} />;
  }

  return (
    <Button
      fullWidth={true}
      startIcon={icon && <DefaultButtonIcon type={icon} />}
      data-test-id={"btn-add-to-wishlist"}
      data-type={"add-to-wishlist"}
      color={color}
      variant={variant}
      {...rest}
    >
      {label}
    </Button>
  );
};

interface OwnProps extends BaseProductElementProps {
  wizardProps?: UseAddProductToWishlistFlowParams;
}

type Props = OwnProps & ButtonBaseProps;

export const BasicAddToWishlistButton = (props: Props) => {
  const product = useProductContext(props);

  const [openModal, modal] = useAddProductToWishlistFlow({
    ...props.wizardProps,
    productInput: product,
  });

  const onClickCb = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      props.onClick?.(e);
      openModal();
    },
    [props.onClick, product.code],
  );

  return (
    <>
      <AddToWishlistButtonBase {...props} onClick={onClickCb} />
      {modal}
    </>
  );
};
